/**
 * 날짜·시각 표기 — 앱의 모든 화면이 이 한 모듈을 쓴다.
 *
 * 화면마다 `toLocaleString` 옵션을 따로 들고 있어 같은 시각이 네 가지로 보였다
 * (`2026. 9. 7. 오전 11:22:36` / `26. 09. 07. 오전 11:22` / `2026년 9월 7일 오전 11:16` /
 * `2026.09.07 11:16`, 게시판 #33). 표기 규칙은 여기에만 있다.
 *
 * 규칙: 한국어 로케일, 24시간제, 자리수 고정 — `2026.09.07` · `11:16` · `11:16:05` · `2026.09.07 11:16` ·
 * `3분 전`(상대 시각, 7일 넘으면 날짜). 값이 없거나 해석 불가면 빈 문자열 — 빈 자리 표기('-', '—')는 화면이 정한다.
 */
export type DateInput = string | number | Date | null | undefined;

const LOCALE = 'ko-KR';

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parts(d: Date, options: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat(LOCALE, options).formatToParts(d)) out[p.type] = p.value;
  return out;
}

const ymd = (d: Date): string => {
  const p = parts(d, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${p.year}.${p.month}.${p.day}`;
};

const hm = (d: Date): string => {
  const p = parts(d, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${p.hour}:${p.minute}`;
};

/** `2026.09.07` */
export function formatDate(value: DateInput): string {
  const d = toDate(value);
  return d ? ymd(d) : '';
}

/** `11:16` (24시간제) */
export function formatTime(value: DateInput): string {
  const d = toDate(value);
  return d ? hm(d) : '';
}

/** `2026.09.07 11:16` */
export function formatDateTime(value: DateInput): string {
  const d = toDate(value);
  return d ? `${ymd(d)} ${hm(d)}` : '';
}

/** `11:16:05` — 초 단위 벽시계(이벤트 로그) */
export function formatClock(value: DateInput): string {
  const d = toDate(value);
  if (!d) return '';
  const p = parts(d, { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  return `${p.hour}:${p.minute}:${p.second}`;
}

/** `방금 전` · `3분 전` · `2시간 전` · `3일 전`, 7일 이상은 `2026.09.07` — 활동 피드 */
export function formatRelative(value: DateInput, now: number = Date.now()): string {
  const d = toDate(value);
  if (!d) return '';
  const diffMin = Math.floor((now - d.getTime()) / 60000);
  if (diffMin < 1) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}일 전`;
  return ymd(d);
}

/** `9월 7일` — 헤더의 오늘 날짜 배지 */
export function formatMonthDay(value: DateInput = new Date()): string {
  const d = toDate(value);
  if (!d) return '';
  const p = parts(d, { month: 'numeric', day: 'numeric' });
  return `${p.month}월 ${p.day}일`;
}

/** `9월 7일 (월)` — 랜딩 인사말 */
export function formatMonthDayWeekday(value: DateInput = new Date()): string {
  const d = toDate(value);
  if (!d) return '';
  const p = parts(d, { month: 'numeric', day: 'numeric', weekday: 'short' });
  return `${p.month}월 ${p.day}일 (${p.weekday})`;
}
